import { useEffect, useRef, useState } from 'react';
import { api } from '../lib/api';
import { useAuth } from '../contexts/AuthContext';
import { Icon, Spinner } from '../components/ui';

// Landing page for the verification link in the signup email:
//   https://<web-app>/#verify-email?token=<token>
function tokenFromHash(): string | null {
  const hash = window.location.hash;
  const queryStart = hash.indexOf('?');
  if (queryStart === -1) return null;
  return new URLSearchParams(hash.slice(queryStart + 1)).get('token');
}

export function VerifyEmail() {
  const { user, refreshUser } = useAuth();
  const [state, setState] = useState<'verifying' | 'success' | 'error'>('verifying');
  const [message, setMessage] = useState('');
  const startedRef = useRef(false);

  useEffect(() => {
    if (startedRef.current) return; // guard against StrictMode double-invoke
    startedRef.current = true;

    const token = tokenFromHash();
    if (!token) {
      setState('error');
      setMessage('This verification link is missing its token. Open the link from your email again.');
      return;
    }

    (async () => {
      const result = await api.verifyEmail(token);
      if (result.success) {
        setState('success');
        setMessage(result.message || 'Email has been verified successfully.');
        if (user) await refreshUser(); // clears the "verify your email" banner
      } else {
        setState('error');
        setMessage(result.error?.message || 'Verification failed. The link may have expired.');
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className="min-h-screen flex items-center justify-center p-6 bg-ink-950">
      <div className="fx w-full max-w-[420px] rounded-2xl border border-ink-700 bg-ink-900 p-8 text-center">
        <div className="flex items-center justify-center gap-[11px] mb-7">
          <span className="w-[13px] h-[13px] rounded-full grad-accent-simple" />
          <span className="font-display font-extrabold tracking-[0.16em] text-white text-[17px]">AUDIARO</span>
        </div>

        {state === 'verifying' && (
          <>
            <div className="flex justify-center mb-4">
              <Spinner size={30} />
            </div>
            <h2 className="font-display font-extrabold text-white text-[22px] mt-0 mb-1.5">
              Verifying your email…
            </h2>
            <p className="text-mist-400 text-sm m-0">Hang tight, this only takes a second.</p>
          </>
        )}

        {state === 'success' && (
          <>
            <span
              className="inline-flex items-center justify-center w-14 h-14 rounded-full mb-4"
              style={{ background: 'rgba(46, 139, 87, .18)', color: '#7BD0A0' }}
            >
              <Icon name="check" size={26} />
            </span>
            <h2 className="font-display font-extrabold text-white text-[22px] mt-0 mb-1.5">
              Email verified
            </h2>
            <p className="text-mist-400 text-sm mt-0 mb-6">{message}</p>
            <a href="#dashboard" className="btn-grad w-full inline-block">
              Go to dashboard
            </a>
          </>
        )}

        {state === 'error' && (
          <>
            <span
              className="inline-flex items-center justify-center w-14 h-14 rounded-full mb-4"
              style={{ background: 'rgba(216, 54, 47, .14)', color: '#F0928C' }}
            >
              <Icon name="plus" size={26} className="rotate-45" />
            </span>
            <h2 className="font-display font-extrabold text-white text-[22px] mt-0 mb-1.5">
              Verification failed
            </h2>
            <p className="text-mist-400 text-sm mt-0 mb-6">{message}</p>
            <a href="#dashboard" className="btn-ghost w-full inline-block">
              Go to dashboard
            </a>
            <p className="text-mist-500 text-[12.5px] mt-4 mb-0">
              You can request a new link from the banner on your dashboard.
            </p>
          </>
        )}
      </div>
    </div>
  );
}
